import React, { useEffect, useRef, useState, useCallback } from 'react';
import { loadGoogleMapsScript } from '../../../utils/googleMapsLoader';
import formStyles from '../Form/Form.module.css';
import styles from './GooglePlacesAutocomplete.module.css';

interface GooglePlacesAutocompleteProps {
  value: string;
  onChange: (address: string, fullAddress: string, lat: number, lng: number, city?: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
  'data-field'?: string;
}

// Extract city from Google Places address_components
const extractCityFromComponents = (addressComponents: any[]): string | undefined => {
  if (!addressComponents || !Array.isArray(addressComponents)) return undefined;

  // Priority order for finding city:
  // 1. locality (city)
  // 2. administrative_area_level_2 (county/district - often used in Turkey)
  // 3. administrative_area_level_1 (state/province)

  let locality: string | undefined;
  let adminLevel2: string | undefined;
  let adminLevel1: string | undefined;

  for (const component of addressComponents) {
    const types = component.types || [];

    if (types.includes('locality')) {
      locality = component.long_name;
    }
    if (types.includes('administrative_area_level_2')) {
      adminLevel2 = component.long_name;
    }
    if (types.includes('administrative_area_level_1')) {
      adminLevel1 = component.long_name;
    }
  }

  // For Turkish addresses, combine district and province (e.g., "Çorlu / Tekirdağ")
  if (adminLevel2 && adminLevel1 && !locality) {
    return `${adminLevel2} / ${adminLevel1}`;
  }

  // Return locality (city) if found
  if (locality) {
    // For some countries, include the state/province
    if (adminLevel1 && adminLevel1 !== locality) {
      return `${locality}, ${adminLevel1}`;
    }
    return locality;
  }

  // Fallback to administrative area
  return adminLevel2 || adminLevel1;
};

interface PlacePrediction {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
    main_text_matched_substrings?: Array<{ offset: number; length: number }>;
  };
}

const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'Enter address',
  className = '',
  disabled = false,
  id,
  'data-field': dataField,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const autocompleteServiceRef = useRef<any>(null);
  const placesServiceRef = useRef<any>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Load Google Maps Places API script using centralized loader
  useEffect(() => {
    loadGoogleMapsScript(['places'])
      .then(() => {
        setScriptLoaded(true);
      })
      .catch(() => {});
  }, []);

  // Initialize services when script is loaded
  useEffect(() => {
    if (!scriptLoaded || disabled) {
      return;
    }

    if (!window.google || !window.google.maps || !window.google.maps.places) {
      return;
    }

    try {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );
    } catch {
      // Google Places initialization failed
    }
  }, [scriptLoaded, disabled]);

  // Get place details
  const getPlaceDetails = useCallback((placeId: string) => {
    if (!placesServiceRef.current) return;

    placesServiceRef.current.getDetails(
      {
        placeId,
        fields: ['formatted_address', 'geometry', 'name', 'address_components'],
      },
      (place: any, status: string) => {
        if (status === 'OK' && place && window.google?.maps?.places) {
          if (place.geometry && place.geometry.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            const formattedAddress = place.formatted_address || '';
            const name = place.name || '';
            const city = extractCityFromComponents(place.address_components);

            onChange(name, formattedAddress, lat, lng, city);
            setIsOpen(false);
            setPredictions([]);
          }
        }
      }
    );
  }, [onChange]);

  // Fetch predictions
  const fetchPredictions = useCallback((input: string) => {
    if (!autocompleteServiceRef.current || !input.trim()) {
      setPredictions([]);
      setIsOpen(false);
      return;
    }

    autocompleteServiceRef.current.getPlacePredictions(
      {
        input,
        types: ['establishment', 'geocode'],
      },
      (predictions: PlacePrediction[] | null, status: string) => {
        if (status === 'OK' && predictions) {
          setPredictions(predictions);
          setIsOpen(true);
          setSelectedIndex(-1);
        } else {
          setPredictions([]);
          setIsOpen(false);
        }
      }
    );
  }, []);

  // Handle input change with debounce
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    onChange(inputValue, inputValue, 0, 0);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchPredictions(inputValue);
    }, 300);
  }, [onChange, fetchPredictions]);

  // Handle selection
  const handleSelect = useCallback((prediction: PlacePrediction) => {
    getPlaceDetails(prediction.place_id);
  }, [getPlaceDetails]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || predictions.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < predictions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < predictions.length) {
          handleSelect(predictions[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setPredictions([]);
        setSelectedIndex(-1);
        break;
    }
  }, [isOpen, predictions, selectedIndex, handleSelect]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Highlight matched text
  const highlightText = (text: string, matchedSubstrings?: Array<{ offset: number; length: number }>) => {
    if (!matchedSubstrings || matchedSubstrings.length === 0) {
      return <span>{text}</span>;
    }

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    matchedSubstrings.forEach((match) => {
      if (match.offset > lastIndex) {
        parts.push(
          <span key={`${lastIndex}-${match.offset}`}>
            {text.substring(lastIndex, match.offset)}
          </span>
        );
      }
      parts.push(
        <span key={`${match.offset}-${match.offset + match.length}`} className={styles.matchedText}>
          {text.substring(match.offset, match.offset + match.length)}
        </span>
      );
      lastIndex = match.offset + match.length;
    });

    if (lastIndex < text.length) {
      parts.push(
        <span key={`${lastIndex}-${text.length}`}>
          {text.substring(lastIndex)}
        </span>
      );
    }

    return <>{parts}</>;
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.autocompleteContainer} ${className}`}
      id={id}
      data-field={dataField}
    >
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (predictions.length > 0) {
            setIsOpen(true);
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        className={formStyles.input}
        style={{ width: '100%' }}
      />
      {isOpen && predictions.length > 0 && (
        <div className={styles.dropdown}>
          {predictions.map((prediction, index) => (
            <button
              key={prediction.place_id}
              type="button"
              className={`${styles.dropdownItem} ${
                index === selectedIndex ? styles.dropdownItemSelected : ''
              }`}
              onClick={() => handleSelect(prediction)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className={styles.dropdownItemContent}>
                <div className={styles.dropdownItemMain}>
                  {highlightText(
                    prediction.structured_formatting.main_text,
                    prediction.structured_formatting.main_text_matched_substrings
                  )}
                </div>
                {prediction.structured_formatting.secondary_text && (
                  <div className={styles.dropdownItemSecondary}>
                    {prediction.structured_formatting.secondary_text}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default GooglePlacesAutocomplete;

